import { CheckCircle, Info, MapPin, Truck, X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClipLoader } from 'react-spinners';
import { AccentButton } from '../../../../../shared/Buttons';
import CityAutocomplete from '../../../../../shared/CityAutocomplete';
import useApi from '../../../../../utils/AxiosWrapper';
import { reverseGeocode } from '../../../../../utils/reverseGeocode';
import useBackdropDismiss from '../../../../../hooks/useBackdropDismiss';
import {
  LOCATION_ERROR_MESSAGES,
  LocationError,
  classifyLocationError,
  getCurrentPosition,
  locationErrorTestId,
} from '../../../../../shared/currentLocation';
import {
  RENTAL_EQUIPMENT_OPTIONS,
  RENTAL_LOCATION_MAX_LENGTH,
  RENTAL_NOTES_MAX_LENGTH,
  RENTAL_REQUEST_ENDPOINT,
  RentalRequestPayload,
} from '../../../../../constants/rentalRequest';

// Rentals V1 — Web. Single portal modal (ReferCarrierModal pattern) with one
// step: fill in whatever you know, submit, done.
//
// Deliberately NOT the Find Me Freight shape. That flow has a review step, a
// terms gate and a fee quote because it commits the carrier to a priced
// dispatch. This one asks TACH to call them back — the extra steps would be
// ceremony around a phone call.
//
// Location DOES follow Find Me Freight: a "Use current location" shortcut over
// a Places-assisted city/ZIP field, sharing that flow's geolocation primitives
// (shared/currentLocation) so the timeouts, the error taxonomy and the recovery
// copy can't drift between the two.
//
// Free text always submits, exactly as it does there: the string is read by a
// human, so Places is an assist, never a gate. Location and equipment are
// required, but "required" means non-empty — a carrier whose city never appears
// in the suggestions, or who is offline from Places entirely, can still type it
// and get their call back.

const LOCATION_LABEL = 'Where do you need it?';
const USE_CURRENT_LOCATION_LABEL = 'Use current location';
const LOCATION_PLACEHOLDER = 'City or ZIP';
const EQUIPMENT_LABEL = 'What are you looking for?';
const EQUIPMENT_PLACEHOLDER = 'Select equipment';
const NOTES_LABEL = 'Anything else?';
const NOTES_PLACEHOLDER =
  'How long you need it, when you need it by, anything else we should know.';

const INTRO_COPY =
  'Tell us what you need and a TACH rep will call you to set it up.';

// Shown after a submit attempt, next to the field that blocked it.
const LOCATION_REQUIRED_ERROR = 'Let us know where you need it.';
const EQUIPMENT_REQUIRED_ERROR = 'Choose what you need.';

const ERROR_COPY = "That didn't send. Check your connection and try again.";

// One class for all three fields so they read as one form. CityAutocomplete
// ships NO styling of its own — its input renders with exactly the classes it
// is handed — so leaving this off is what makes it a bare browser input next to
// two styled ones. Matches the Find Me Freight sheet's `inputFieldClass`,
// including the `accent` focus ring (the brand token, not a raw Tailwind color).
const INPUT_FIELD_CLASS =
  'w-full rounded-lg border bg-gray-100 p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-accent';

// Marks a field the submit gate actually requires. The glyph is decorative —
// screen readers get the word instead, since a bare "*" is announced
// inconsistently (or not at all). Same treatment as the Find Me Freight sheet.
const RequiredMark = () => (
  <>
    <span aria-hidden className="ml-0.5 text-danger">
      *
    </span>
    <span className="sr-only"> (required)</span>
  </>
);

/** Inline, field-level validation message. */
const FieldError = ({
  testId,
  message,
}: {
  testId: string;
  message: string;
}) => (
  <p data-testid={testId} className="text-xs text-danger">
    {message}
  </p>
);

const RentalRequestModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { callProtectedApi } = useApi();

  const [location, setLocation] = useState('');
  const [locationError, setLocationError] = useState<LocationError>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  /**
   * Errors stay hidden until the first submit attempt. Showing "pick your city"
   * while someone is mid-word in the field is nagging, not helping.
   */
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [equipmentType, setEquipmentType] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Guards against a double-submit from an impatient second click — the button
  // is disabled while submitting, but a ref closes the window between the click
  // and the re-render.
  const isSubmittingRef = useRef(false);
  // The user can dismiss the modal mid-flight. The POST still lands (an admin
  // gets the request either way), we just stop narrating its outcome into a
  // modal that is already gone.
  const isAbandonedRef = useRef(false);

  useEffect(() => {
    isAbandonedRef.current = false;
    return () => {
      isAbandonedRef.current = true;
    };
  }, []);

  const handleClose = useCallback(() => {
    isAbandonedRef.current = true;
    onClose();
  }, [onClose]);

  const backdropDismiss = useBackdropDismiss(handleClose);

  // Esc closes, matching every other modal in the dashboard.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  // Resolve a fix, reverse-geocode it to a readable city, and drop that INTO
  // the text field so editing or clearing it afterwards behaves exactly like
  // something typed. A failure is an amber note, not an error — the field below
  // already answers the question.
  const useCurrentLocation = useCallback(async () => {
    setLocationLoading(true);
    setLocationError(null);
    try {
      const position = await getCurrentPosition();
      if (isAbandonedRef.current) return;
      const { latitude, longitude } = position.coords;

      const geo = await reverseGeocode(latitude, longitude);
      if (isAbandonedRef.current) return;
      // Coordinates are the last resort: a rep can still act on them, and
      // filling nothing would read as a broken button.
      const display = (
        geo?.display ?? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
      ).slice(0, RENTAL_LOCATION_MAX_LENGTH);
      setLocation(display);
    } catch (error) {
      if (isAbandonedRef.current) return;
      setLocationError(classifyLocationError(error));
    } finally {
      if (!isAbandonedRef.current) setLocationLoading(false);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) return;

    // Validate before anything else — a blocked submit must not clear the
    // network error banner or flip the button into its loading state.
    setAttemptedSubmit(true);
    if (!location.trim() || !equipmentType) return;

    isSubmittingRef.current = true;
    setHasError(false);
    setSubmitting(true);

    // Blank fields are omitted rather than sent as '': the server treats both
    // the same, but a payload that carries only what the carrier actually
    // typed is the one that reads correctly in a network log.
    // Notes stay optional, so a blank one is omitted rather than sent as ''.
    const payload: RentalRequestPayload = {
      location: location.trim(),
      equipment_type: equipmentType,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };

    try {
      const response = await callProtectedApi({
        url: RENTAL_REQUEST_ENDPOINT,
        requestType: 'post',
        body: payload,
        errorMsg: 'Something went wrong sending your rental request.',
        // Suppress AxiosWrapper's toast — this modal renders its own in-context
        // error banner with a Retry action right under the button, and letting
        // the toast fire too would show two competing messages for one failure.
        hideAlert: true,
      });
      const status = response?.status;
      if (isAbandonedRef.current) return;
      if (
        status !== undefined &&
        status >= HttpStatusCode.Ok &&
        status < HttpStatusCode.MultipleChoices
      ) {
        setSubmitted(true);
      } else {
        setHasError(true);
      }
    } catch (e) {
      console.error('Error occurred while submitting rental request: ', e);
      if (!isAbandonedRef.current) setHasError(true);
    } finally {
      isSubmittingRef.current = false;
      if (!isAbandonedRef.current) setSubmitting(false);
    }
  }, [callProtectedApi, location, equipmentType, notes]);

  if (!isOpen) return null;

  const renderForm = () => (
    <div className="flex flex-col gap-4 p-4">
      <p className="text-sm text-gray-600">{INTRO_COPY}</p>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="rentalRequest:location"
          className="text-sm text-gray-500"
        >
          {LOCATION_LABEL}
          <RequiredMark />
        </label>
        <button
          type="button"
          onClick={useCurrentLocation}
          disabled={locationLoading}
          data-testid="rentalRequest:useCurrentLocation"
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-accent bg-white p-3 text-sm font-medium text-primary hover:bg-bgyellow disabled:cursor-not-allowed disabled:opacity-60"
        >
          {locationLoading ? (
            <ClipLoader color="#F7A826" size={16} data-testid="loader" />
          ) : (
            <>
              <MapPin size={18} />
              <span>{USE_CURRENT_LOCATION_LABEL}</span>
            </>
          )}
        </button>

        {locationError && (
          // Amber note, not a red error: the field below is a first-class way
          // to answer this question, so a failed lookup is a detour, not a fault.
          <div
            data-testid={locationErrorTestId('rentalRequest', locationError)}
            className="flex items-start gap-2 rounded-lg bg-bgyellow p-2.5 text-xs text-gray-600"
          >
            <Info
              size={14}
              weight="fill"
              className="mt-px flex-shrink-0 text-accent"
            />
            <span>{LOCATION_ERROR_MESSAGES[locationError]}</span>
          </div>
        )}

        <CityAutocomplete
          id="rentalRequest:location"
          testId="rentalRequest:location"
          value={location}
          // CityAutocomplete takes no maxLength (it is a free-text input with a
          // portaled dropdown), so the server's cap is applied here instead —
          // otherwise a paste longer than the limit only fails on submit.
          onChange={(text) =>
            setLocation(text.slice(0, RENTAL_LOCATION_MAX_LENGTH))
          }
          onSelect={(selection) =>
            setLocation(selection.display.slice(0, RENTAL_LOCATION_MAX_LENGTH))
          }
          placeholder={LOCATION_PLACEHOLDER}
          addClass={INPUT_FIELD_CLASS}
        />
        {attemptedSubmit && !location.trim() && (
          <FieldError
            testId="rentalRequest:locationRequired"
            message={LOCATION_REQUIRED_ERROR}
          />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="rentalRequest:equipment"
          className="text-sm text-gray-500"
        >
          {EQUIPMENT_LABEL}
          <RequiredMark />
        </label>
        <select
          id="rentalRequest:equipment"
          data-testid="rentalRequest:equipment"
          value={equipmentType}
          onChange={(e) => setEquipmentType(e.target.value)}
          className={INPUT_FIELD_CLASS}
        >
          <option value="">{EQUIPMENT_PLACEHOLDER}</option>
          {RENTAL_EQUIPMENT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {attemptedSubmit && !equipmentType && (
          <FieldError
            testId="rentalRequest:equipmentRequired"
            message={EQUIPMENT_REQUIRED_ERROR}
          />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="rentalRequest:notes" className="text-sm text-gray-500">
          {NOTES_LABEL}
        </label>
        <textarea
          id="rentalRequest:notes"
          data-testid="rentalRequest:notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={RENTAL_NOTES_MAX_LENGTH}
          rows={3}
          placeholder={NOTES_PLACEHOLDER}
          className={`${INPUT_FIELD_CLASS} resize-none`}
        />
      </div>

      {hasError && (
        <p
          data-testid="rentalRequest:error"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-700"
        >
          {ERROR_COPY}
        </p>
      )}

      <div data-testid="rentalRequest:submit">
        <AccentButton
          onClick={handleSubmit}
          disabled={submitting}
          addClass="w-full rounded-full py-3"
        >
          {submitting ? (
            <ClipLoader color="#1D213B" size={18} data-testid="loader" />
          ) : hasError ? (
            'Retry'
          ) : (
            'Request a rental'
          )}
        </AccentButton>
      </div>
    </div>
  );

  const renderSuccessState = () => (
    <div
      data-testid="rentalRequest:successView"
      className="flex flex-col items-center justify-center px-6 py-10 text-center"
    >
      <CheckCircle size={64} weight="fill" className="mb-4 text-green-600" />
      <h3 className="mb-2 text-lg font-semibold text-primary">
        We got it. We&apos;ll call you.
      </h3>
      <p className="mb-6 text-sm text-gray-600">
        Someone from TACH will reach out to get your rental set up. Please keep
        your phone handy.
      </p>
      <AccentButton onClick={handleClose} addClass="w-full rounded-full py-3">
        Got it
      </AccentButton>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50"
      data-testid="rentalRequest:backdrop"
      // Clicking outside the panel closes it (see useBackdropDismiss).
      {...backdropDismiss}
    >
      <div className="max-h-[90%] w-full overflow-y-auto overflow-x-hidden rounded-[4px] bg-white sm:max-w-[95%] md:max-w-[500px]">
        <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-[#EAECF0] p-4 align-middle">
          <div className="flex items-center gap-2">
            <Truck size={20} className="text-primary" />
            <h2 className="text-[16px] font-[600] text-primary">
              {submitted ? 'Request sent' : 'Request a Rental'}
            </h2>
          </div>
          <X
            className="hover:cursor-pointer hover:opacity-80"
            onClick={handleClose}
            weight="regular"
            size={24}
            data-testid="rentalRequest:close"
          />
        </div>

        {submitted ? renderSuccessState() : renderForm()}
      </div>
    </div>,
    document.body
  );
};

export default RentalRequestModal;
